/**
 * What changed, version by version, newest first, written for the people who
 * use the app (pt-BR). Not every version has an entry, only the ones worth
 * telling about, added by the commit that makes the version (see "Versioning"
 * in ARCHITECTURE.md); the minor ones (`x.N.0`) are the core features.
 *
 * @typedef {{version: string, date: string, changes: string[]}} ChangelogEntry
 * @type {ChangelogEntry[]}
 */
export const CHANGELOG = [
    {
        version: "3.0.2",
        date: "2026-10-08",
        changes: [
            "Nos detalhes da ordem de serviço, inclusive no link do cliente, os textos longos (como os equipamentos do cliente) não são mais cortados com reticências: agora quebram em mais linhas e aparecem por inteiro.",
        ],
    },
    {
        version: "2.1.0",
        date: "2026-10-07",
        changes: [
            "Seu cliente agora pode pagar pelo link da ordem: depois que a ordem é confirmada, e até ser paga, aparecem os botões para copiar o código Pix ou mostrar o QR code. Eles só aparecem se você tiver cadastrado sua chave Pix em Configurações.",
            "No menu Compartilhar da ordem, o novo item QR code Pix mostra o código para o cliente ler com a câmera ou com o app do banco.",
            "A versão para imprimir traz o QR code do Pix ao lado do total, nas ordens confirmadas ou concluídas.",
        ],
    },
    {
        version: "2.0.3",
        date: "2026-10-07",
        changes: [
            "O Ordo ficou mais confortável no celular: as listas de ordens, clientes, serviços, produtos e tipos de equipamento cabem na tela sem rolar para o lado, em cartões compactos (nas ordens e nos itens, a situação aparece como uma faixa colorida na lateral).",
            "No celular, o menu, o cabeçalho da ordem de serviço, os botões de Configurações e os Avisos (que agora abrem a página inteira) foram reorganizados para não ficarem apertados.",
            "A ordem de serviço para imprimir ficou mais compacta, para caber em uma página.",
        ],
    },
    {
        version: "2.0.0",
        date: "2026-10-06",
        changes: [
            "Nova página Contabilidade, no menu da sua conta: receita, custo, lucro, margem e ticket médio, o dinheiro a caminho (em negociação, confirmado, a receber e recebido) e a previsão do mês.",
            "Gráficos mês a mês, cada um respondendo uma pergunta: receita e lucro, ordens, ticket médio, margem, serviços e produtos. Cada um traz uma linha de tendência, com a velocidade em palavras, e mostra no detalhe o que ainda está a caminho.",
            "A página Início ficou mais leve e ganhou um item no menu: mostra o que precisa de atenção (orçamentos vencendo ou sem resposta, ordens para concluir e para receber), como está o mês e as ordens recentes.",
        ],
    },
    {
        version: "1.14.0",
        date: "2026-10-06",
        changes: [
            "Novo cadastro de Produtos (peças, acessórios...), com preço e custo, que entram nas ordens ao lado dos serviços, no link do cliente, na versão para imprimir e no Recibo de Retirada.",
            "Serviços também ganham custo. A ordem mostra o custo total e a margem de ganho (só para você: o cliente não vê).",
            "Atualizar preços agora pode ser feito separadamente para serviços e para produtos.",
        ],
    },
    {
        version: "1.13.0",
        date: "2026-10-06",
        changes: [
            "Nova versão da ordem de serviço para imprimir (A4, preto e branco), pelo menu Ações, já com o Recibo de Retirada para o cliente assinar ao receber seus equipamentos.",
        ],
    },
    {
        version: "1.12.0",
        date: "2026-10-05",
        changes: [
            "Novos estilos para o aplicativo (Flatly, Materia e Sketchy, em beta), escolhidos em Configurações ao lado da cor.",
        ],
    },
    {
        version: "1.11.6",
        date: "2026-10-05",
        changes: [
            "Novo tema Arco-íris, cabeçalhos e cartões na cor do tema, e a escolha de cor agora fica em Configurações, com prévia.",
        ],
    },
    {
        version: "1.11.3",
        date: "2026-10-05",
        changes: ["Nova página com o histórico de mudanças, aberta ao clicar na versão do Ordo."],
    },
    {
        version: "1.11.0",
        date: "2026-10-05",
        changes: [
            "Caixa de avisos em tela cheia: tudo o que os clientes e o sistema fizeram com as suas ordens, com filtro por tipo e por não lidos.",
        ],
    },
    {
        version: "1.10.0",
        date: "2026-10-05",
        changes: [
            "Histórico permanente de cada ordem, aberto ao clicar em “Criada em …”.",
            "Sino no topo avisa quando um cliente confirma ou cancela uma ordem, ou quando ela expira.",
        ],
    },
    {
        version: "1.9.0",
        date: "2026-10-05",
        changes: ["Escolha da cor de destaque do aplicativo: rosa, floresta ou tangerina."],
    },
    {
        version: "1.8.0",
        date: "2026-10-05",
        changes: [
            "Ordens abertas ou enviadas podem ser editadas e ter os preços atualizados.",
            "Ações da ordem reorganizadas em menus, com ícones.",
        ],
    },
    {
        version: "1.7.0",
        date: "2026-10-05",
        changes: ["Ordens enviadas que o cliente não respondeu passam a expirar sozinhas."],
    },
    {
        version: "1.6.0",
        date: "2026-10-05",
        changes: ["Envio de ordens: o cliente abre um link, vê a ordem e a confirma ou cancela."],
    },
    {
        version: "1.5.0",
        date: "2026-10-05",
        changes: ["Navegação para a ordem anterior e a próxima, na página da ordem."],
    },
    {
        version: "1.4.0",
        date: "2026-10-04",
        changes: ["Gráfico de ordens e faturamento dos últimos 12 meses na página inicial."],
    },
    {
        version: "1.3.0",
        date: "2026-10-04",
        changes: ["As listas lembram filtros, busca, ordenação e página ao voltar de uma ordem, cliente ou item."],
    },
    {
        version: "1.2.0",
        date: "2026-10-04",
        changes: ["Números de ordens e faturamento na página inicial."],
    },
    {
        version: "1.1.0",
        date: "2026-10-04",
        changes: ["Troca de senha dentro do aplicativo."],
    },
    {
        version: "1.0.0",
        date: "2026-10-04",
        changes: ["Primeira versão em produção: ordens de serviço, clientes, itens de serviço e tipos de dispositivo."],
    },
];
